import {
	faBoxesStacked,
	faCircleExclamation,
	faFileInvoiceDollar,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthGate } from '../../../hooks/useAuthGate';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontAccountSnapshot } from '../../../services/portalDashboardService';

/**
 * Account Snapshot widget — open balance, credit limit, available
 * credit, optional credit-hold flag. Two CTA links route to the
 * Invoices and Account pages where the customer can drill in.
 *
 * Login-required. For anonymous visitors the widget renders a sign-in
 * prompt rather than disappearing — the homepage stays usable for
 * browsing, and the widget doubles as a sign-in conversion surface.
 */
export function AccountSnapshotWidget({
	title,
}: {
	title?: string;
}): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const requireAuth = useAuthGate();
	const { token } = usePortalAuth();

	const { data, isLoading, isError } = useQuery({
		queryKey: ['portal', 'dashboard', 'account-snapshot'],
		queryFn: () => getStorefrontAccountSnapshot(token),
		enabled: token != null,
		staleTime: 60_000,
	});

	const snapshot = data?.data ?? null;

	return (
		<section className="w-full h-full flex flex-col bg-white md:bg-brand-card dark:bg-brand-card border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center justify-between mb-4">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon icon={faUser} className="text-portal-accent" />
					{t(title ?? 'Account')}
				</h2>
			</header>

			{token == null ? (
				<div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
					<p className="text-sm text-brand-muted max-w-xs">
						{t('Sign in to see your balance, credit, and recent activity.')}
					</p>
					<button
						type="button"
						onClick={() => requireAuth()}
						className="px-4 py-2 text-sm font-medium rounded-md bg-portal-accent text-white hover:opacity-90"
					>
						{t('Sign in')}
					</button>
				</div>
			) : isLoading ? (
				<EmptyMessage message={t('Loading account...')} />
			) : isError || snapshot == null ? (
				<EmptyMessage message={t("We couldn't load your account right now.")} />
			) : (
				<>
					<div className="flex-1 grid grid-cols-2 gap-4">
						<MetricBlock
							label={t('Open balance')}
							value={formatCurrency(snapshot.openBalance, t)}
						/>
						<MetricBlock
							label={t('Available credit')}
							value={
								snapshot.availableCredit != null
									? formatCurrency(snapshot.availableCredit, t)
									: t('—')
							}
							sublabel={
								snapshot.creditLimit != null
									? `${t('Limit')}: ${formatCurrency(snapshot.creditLimit, t)}`
									: undefined
							}
						/>
					</div>

					{snapshot.hasCreditHold && (
						<div className="mt-3 p-2 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 flex items-start gap-2 text-xs text-yellow-800 dark:text-yellow-200">
							<FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5" />
							<span>
								{t(
									'Your account is currently on credit hold. Contact your sales rep for details.',
								)}
							</span>
						</div>
					)}

					{/* Quick links: Invoices + Orders. Account access lives in
					    the BottomNav (mobile) / UserMenu (desktop), so we
					    don't duplicate it here — the two slots are better
					    spent on the customer's two financial-history pages. */}
					<div className="mt-4 grid grid-cols-2 gap-2">
						<button
							type="button"
							onClick={() => navigate('/invoices')}
							className="px-3 py-2 text-xs md:text-sm font-medium rounded-md border border-brand-border bg-brand-input hover:bg-brand-card inline-flex items-center justify-center gap-2"
						>
							<FontAwesomeIcon icon={faFileInvoiceDollar} />
							{t('Invoices')}
						</button>
						<button
							type="button"
							onClick={() => navigate('/orders')}
							className="px-3 py-2 text-xs md:text-sm font-medium rounded-md border border-brand-border bg-brand-input hover:bg-brand-card inline-flex items-center justify-center gap-2"
						>
							<FontAwesomeIcon icon={faBoxesStacked} />
							{t('Orders')}
						</button>
					</div>
				</>
			)}
		</section>
	);
}

function MetricBlock({
	label,
	value,
	sublabel,
}: {
	label: string;
	value: string;
	sublabel?: string;
}): JSX.Element {
	return (
		<div>
			<p className="text-[11px] uppercase tracking-wide text-brand-muted">
				{label}
			</p>
			<p className="mt-0.5 text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
				{value}
			</p>
			{sublabel != null && (
				<p className="text-[11px] text-brand-muted mt-0.5">{sublabel}</p>
			)}
		</div>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}

function formatCurrency(amount: number, t: (s: string) => string): string {
	const sign = amount < 0 ? '-' : '';
	const absAmount = Math.abs(amount);
	return `${sign}${t('$')}${absAmount.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}
